import { toast } from 'react-toastify';
import { isEmpty } from 'lodash';

import { callApi } from '../../../../apiCaller';
import { cloneDeep, toastError } from '../../../../utils';
import {
     API_SYSTEM_ADMIN_ORDER_DETAIL,
     API_SYSTEM_ADMIN_ORDER,
     DEFAULT_FN,
     PAYMENT_STATUS_ORDER,
     SEAT_STATUS,
     SHIPPING_CARRIER,
     SHIPPING_STATUS,
     POPUP_TYPE,
     API_SYSTEM_ADMIN_ORDER_REFUND,
     API_SYSTEM_ADMIN_ORDER_REFUND_DETAIL,
     ORDER_STATUS,
     TRAINING_PRODUCT_TYPES,
} from '../../../../constants';
import { setIsWaiting, setRemoveLoading, setShow, setShowRemovePopup } from '../../../../actions/general';
import { OrderAPI } from '../../../../services/apis/orderAPI';

export const ADD_ADMIN_ORDERS = 'addAdminOrders';
export const UPDATE_ADMIN_ORDER = 'updateAdminOrder';
export const DELETE_ADMIN_ORDER = 'deleteAdminOrder';
export const UPDATE_ADMIN_SEAT = 'updateAdminSeat';

export const API_SYSTEM_ADMIN_ORDER_REMOVE_SEAT = 'order/:id/cancel-remove-seat';

const API_SYSTEM_ADMIN_REMOVE_ORDER_SEAT = 'seat/admin-remove-order-seat/:id';

// Get All
export const addAdminOrders = (orders) => {
     return {
          type: ADD_ADMIN_ORDERS,
          payload: orders,
     };
};

export const fetchAdminOrdersRequest = (nextDispatch) => (dispatch) => {
     const { setIsLoadingFilter, paginationPayload } = nextDispatch;

     const { page, pageSize, filter, sortBy, sortType } = paginationPayload;

     const { optionsHide, searchFilterColumns, category } = filter;

     const checkOptionsHide = Object.values(optionsHide).some((value) => value);
     const checkSearchFilterColumns = isEmpty(searchFilterColumns);

     const queryFilter =
          checkOptionsHide || !checkSearchFilterColumns
               ? `&filter=${encodeURIComponent(JSON.stringify(filter))}`
               : category
               ? `&filter=${encodeURIComponent(JSON.stringify({ category }))}`
               : '';

     setIsLoadingFilter(true);

     const endpoint = `${API_SYSTEM_ADMIN_ORDER}?page=${page}&pageSize=${pageSize}${queryFilter}&sortBy=${sortBy}&sortType=${sortType}`;
     callApi(endpoint, 'GET')
          .then((response) => {
               if (response.status !== 200) {
                    toastError(response);
                    return;
               }

               const { rows, count } = response.data;

               const newOrder = {
                    rows: rows.map((order) => ({
                         ...order,
                         organizationName: order.organization?.name,
                    })),
                    count,
               };

               dispatch(addAdminOrders(newOrder));
          })
          .finally(() => setIsLoadingFilter(false));
};

export const fetchAdminOrderDetailRequest =
     (orderId, callBack = DEFAULT_FN) =>
     async () => {
          return await callApi(API_SYSTEM_ADMIN_ORDER_DETAIL.replace(':id', orderId), 'GET').then((response) => {
               if (response.status !== 200) {
                    toastError(response);
                    return;
               }
               callBack(response.data);
          });
     };

export const checkingGeneratedVoucherUsed = async (payload) => {
     const { id, selectedProductIds, setCheckingResult } = payload;

     const result = await OrderAPI.checkGeneratedVoucherUsed(id, selectedProductIds);
     if (!result) {
          return;
     }

     setCheckingResult(result);
};

// Update
export const updateAdminOrder = (orderId, order) => {
     return {
          type: UPDATE_ADMIN_ORDER,
          payload: {
               orderId,
               order,
          },
     };
};

export const updateAdminOrderRequest = (orderId, newOrder, nextDispatch) => async (dispatch) => {
     const {
          physicalSeats = [],
          setOrder = DEFAULT_FN,
          setPhysicalSeats = DEFAULT_FN,
          setShowRefund = DEFAULT_FN,
          setIsDisableChecked = DEFAULT_FN,
          setShowPaymentCancelNote = DEFAULT_FN,
          setShowReleaseSeat = DEFAULT_FN,
     } = nextDispatch || {};

     const newPhysicalSeats = cloneDeep(physicalSeats);
     const seatsPayload = [];
     let isAllPhysicalSeatsShipped = !!newPhysicalSeats.length;

     newPhysicalSeats.reverse();

     for (let i = 0; i < newPhysicalSeats.length; i++) {
          const seat = newPhysicalSeats[i];

          const shippingStatusShipped = seat.shippingStatus === SHIPPING_STATUS.SHIPPED;
          if (!shippingStatusShipped) isAllPhysicalSeatsShipped = false;

          const seatPayload = {
               id: seat.id,
               userId: seat.user?.id || null,
               emailInvite: seat.user?.email || null,
               trackingCode: shippingStatusShipped ? seat.trackingCode : '',
               trackingCarrierCode: shippingStatusShipped ? seat.trackingCarrierCode : SHIPPING_CARRIER.UNKNOWN,
               shippingStatus: seat.shippingStatus,
               shipment: seat.shipment,
          };

          seatsPayload.push(seatPayload);
          newPhysicalSeats[i] = {
               ...seat,
               ...seatPayload,
          };
     }

     const status = newOrder.status;
     const newShippingStatus = isAllPhysicalSeatsShipped ? SHIPPING_STATUS.SHIPPED : newOrder.shippingStatus;
     const newAdditionalNotes = status === ORDER_STATUS.CANCELED ? ORDER_STATUS.CANCELED : newOrder.additionalNotes;

     const orderPayload = {
          id: newOrder.id,
          shippingStatus: newShippingStatus,
          status,
          orderItems: newOrder.orderItems,
          seats: seatsPayload,
          releaseSeat: newOrder.releaseSeat,
          additionalNotes: newAdditionalNotes || null,
     };

     dispatch(setIsWaiting(true));

     return await callApi(API_SYSTEM_ADMIN_ORDER_DETAIL.replace(':id', orderId), 'PUT', orderPayload)
          .then((response) => {
               if (response.status !== 200) {
                    setIsDisableChecked(false);
                    setShowReleaseSeat(true);
                    dispatch(setShow(POPUP_TYPE.WARNING_REMAINING_SEATS));
                    return;
               }

               const orderUpdated = response.data;

               if (newOrder.status === ORDER_STATUS.COMPLETED) {
                    setShowRefund(true);
               }

               if (newPhysicalSeats.length) {
                    setPhysicalSeats(newPhysicalSeats);
               }

               setOrder((currentOrder) => ({
                    ...currentOrder,
                    shippingStatus: orderUpdated.shippingStatus,
                    status: orderUpdated.status,
                    paymentAt: orderUpdated.paymentAt,
                    additionalNotes: orderUpdated.additionalNotes,
               }));

               dispatch(updateAdminOrder(orderId, orderUpdated));
               dispatch(setShow(null));

               if (newOrder.status === ORDER_STATUS.CANCELED) {
                    setShowPaymentCancelNote(true);
               }

               return orderUpdated;
          })
          .finally(() => {
               dispatch(setIsWaiting(false));
          });
};

export const updateAdminORderRefund = (id, payload, nextDispatch) => async (dispatch) => {
     const { setLoading, setShowRemoveSeat } = nextDispatch;

     dispatch(setIsWaiting(true));

     return await callApi(API_SYSTEM_ADMIN_ORDER_REFUND_DETAIL.replace(':id', id), 'PUT', payload)
          .then((response) => {
               setLoading(false);
               setShowRemoveSeat(false);

               if (response.status !== 200) {
                    toastError(response);
                    return;
               }

               toast.success('Remove seat successfully');
          })
          .finally(() => {
               dispatch(setIsWaiting(false));
          });
};

export const createAdminOrderRequest = (payload) => (dispatch) => {
     const { initialValues, initOrderDetail, nextDispatch } = payload;
     const { id, productSelected, totalRefund, refundAll, refundDescription } = nextDispatch;

     dispatch(setIsWaiting(true));
     callApi(API_SYSTEM_ADMIN_ORDER_REFUND, 'POST', nextDispatch)
          .then((response) => {
               if (response.status !== 200) {
                    toastError(response);
                    return;
               }

               payload.setLoading(false);
               payload.setProductSelected(null);
               payload.setOptionSelected(null);

               if (productSelected) {
                    payload.setShowRemoveSeat(true);
               }
               payload.setListOption(payload.arrangedOrderItems);
               payload.setOptionDefault(null);
               payload.setShowConfirmRefund(false);
               const orderItemSelected = productSelected || [];

               for (let i = 0; i < initialValues.orderItems?.length; i++) {
                    for (let j = 0; j < orderItemSelected.length; j++) {
                         if (initialValues.orderItems[i].id !== orderItemSelected[j].id) continue;

                         if (!orderItemSelected[j].remainingRefund) {
                              initialValues.orderItems[i].remainingRefund = orderItemSelected[j].quantity - orderItemSelected[j].quantityRefund;
                         } else {
                              initialValues.orderItems[i].remainingRefund =
                                   orderItemSelected[j].remainingRefund - orderItemSelected[j].quantityRefund;
                         }
                    }
               }

               initialValues.totalAmount = totalRefund + initialValues.totalAmount;
               initialValues.refundAmount = refundAll;

               if (refundDescription !== '') {
                    initialValues.refundDescription = refundDescription;
               }
               payload.setOrder(initialValues);
               dispatch(fetchAdminOrderDetailRequest(id, initOrderDetail));

               toast.success('Create refund successfully');
          })
          .finally(() => {
               dispatch(setIsWaiting(false));
          });
};

export const updateAminOrderBulkShipping = (orderId, values, nextDispatch) => (dispatch) => {
     const { initialValues, initOrderDetail, physicalSeats, setSubmitting } = nextDispatch;

     const seatsPayload = [];
     const shippingStatusShipped = values.shippingStatus === SHIPPING_STATUS.SHIPPED;

     for (let i = 0; i < physicalSeats.length; i++) {
          const seat = physicalSeats[i];
          const statusSeat = seat.status !== SEAT_STATUS.RESERVED && seat.status !== SEAT_STATUS.OPEN ? true : false;
          const seatPayload = {
               id: seat.id,
               userId: seat.user?.id || null,
               emailInvite: seat.user?.email || null,
               trackingCode: shippingStatusShipped ? values.trackingCode : '',
               trackingCarrierCode: shippingStatusShipped ? values.trackingCarrierCode : SHIPPING_CARRIER.UNKNOWN,
               shippingStatus: statusSeat ? values.shippingStatus : SHIPPING_STATUS.NOT_SHIPPED,
          };

          seatsPayload.push(seatPayload);
     }

     const orderPayload = {
          id: initialValues.id,
          status: initialValues.status,
          orderItems: initialValues.orderItems,
          seats: seatsPayload,
          shippingStatus: values.shippingStatus,
     };

     dispatch(setIsWaiting(true));

     callApi(API_SYSTEM_ADMIN_ORDER_DETAIL.replace(':id', orderId), 'PUT', orderPayload)
          .then((response) => {
               setSubmitting(false);

               if (response.status !== 200) {
                    toastError(response);
                    return;
               }

               dispatch(updateAdminOrder(orderId, response.data));
               dispatch(fetchAdminOrderDetailRequest(orderId, initOrderDetail));

               toast.success('Update successfully');
          })
          .finally(() => {
               dispatch(setIsWaiting(false));
          });
};

// Delete
export const deleteAdminOrder = (orderId) => {
     return {
          type: DELETE_ADMIN_ORDER,
          payload: orderId,
     };
};

export const deleteAdminOrderRequest = (orderId, navigateToOrders) => (dispatch, getState) => {
     const orders = getState().admin.orders;
     const order = orders?.rows?.find((order) => order.id === orderId);

     dispatch(setRemoveLoading(false));
     callApi(API_SYSTEM_ADMIN_ORDER_DETAIL.replace(':id', orderId), 'DELETE')
          .then((response) => {
               if (response.status !== 200) {
                    toastError(response);
                    return;
               }

               if (order) {
                    order.status = PAYMENT_STATUS_ORDER.CANCELED;
                    dispatch(updateAdminOrder(orderId, order));
               }

               toast.success('Deleted successfully');
          })
          .finally(() => {
               dispatch(setRemoveLoading(false));
               dispatch(setShowRemovePopup(null));
               navigateToOrders();
          });
};

export const updatePaymentCancelNote = (id, nextDispatch) => (dispatch) => {
     const { paymentCancelNote, setPaymentCancelNote, setShowPaymentCancelNote, setOrder, showPopup } = nextDispatch;

     const orderPayload = {
          status: ORDER_STATUS.CANCELED,
          paymentCancelNote: paymentCancelNote,
     };

     dispatch(setIsWaiting(true));

     callApi(API_SYSTEM_ADMIN_ORDER_DETAIL.replace(':id', id), 'PUT', orderPayload)
          .then((response) => {
               setIsWaiting(false);

               if (response.status !== 200) {
                    dispatch(setShow(POPUP_TYPE.WARNING_REMAINING_SEATS));
                    return;
               }

               const orderUpdated = response.data;

               setOrder((currentOrder) => ({
                    ...currentOrder,
                    paymentCancelNote: paymentCancelNote,
               }));
               setPaymentCancelNote(paymentCancelNote);
               setShowPaymentCancelNote(false);
               dispatch(updateAdminOrder(id, orderUpdated));
               if (showPopup) {
                    dispatch(setShow(showPopup));
               } else {
                    toast.success('Update successfully');
               }

               return orderUpdated;
          })
          .finally(() => {
               dispatch(setIsWaiting(false));
          });
};

// Remove
export const removeAdminSeatCancelRequest = (orderId) => async (dispatch) => {
     return await callApi(API_SYSTEM_ADMIN_ORDER_REMOVE_SEAT.replace(':id', orderId), 'DELETE').then((response) => {
          if (response.status !== 200) {
               toastError(response);
               return;
          }

          dispatch(setShow(null));
          toast.success('Seat deleted successfully');

          return response.data;
     });
};

export const adminRemoveOrderSeatRequest = async (seatId, nextDispatch) => {
     const { setShowConfirmRemoveSeatPopup, physicalSeats, setPhysicalSeats, linkSeats, setLinkSeats, initRestSeats, setInitRestSeats } =
          nextDispatch;

     return await callApi(API_SYSTEM_ADMIN_REMOVE_ORDER_SEAT.replace(':id', seatId), 'DELETE').then((response) => {
          if (response.status !== 200) {
               toastError(response);
               return;
          }

          const productType = response.data;

          switch (productType) {
               case TRAINING_PRODUCT_TYPES.PHYSICAL_PRODUCT: {
                    const newPhysicalSeats = physicalSeats.filter((seat) => seat.id !== seatId);
                    setPhysicalSeats(newPhysicalSeats);
                    break;
               }
               case TRAINING_PRODUCT_TYPES.PURCHASE_DIGITAL_DOWNLOAD: {
                    const newLinkSeats = linkSeats.filter((link) => link.seatId !== seatId);
                    setLinkSeats(newLinkSeats);
                    break;
               }
               default: {
                    const newResSeats = initRestSeats.filter((seat) => seat.id !== seatId);
                    setInitRestSeats(newResSeats);
                    break;
               }
          }

          setShowConfirmRemoveSeatPopup(null);
          toast.success('Seat deleted successfully');

          return response.data;
     });
};
